import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  SnackbarContent,
  IconButton,
  CircularProgress,
  Tooltip,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Paper,
  DialogActions,
} from "@mui/material";
import ReactMarkdown from "react-markdown";

import colors from "../../../../Constants/colors";
import { useDispatch, useSelector } from "react-redux";

import {
  deleteBotFromOrg,
  generate_APIKey,
  getOrganization,
  modifyKeys,
} from "../../../../features/organization/orgSlice";

import {
  getCombinations,
  saveDefaultCombinations,
} from "../../../../features/chatbots/chatbotSlice";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import "./chatwindow.css";
import { loaderStyles } from "../TemplatesStyles";
import SendIcon from "@mui/icons-material/Send";
import remarkGfm from "remark-gfm";
import theme, {
  createLLMTypography,
  scrollbarStyles,
} from "../../../../Constants/theme";

import {
  iconButtonActive,
  primaryButtonStyles,
  textButtonStyles,
} from "../../../../Constants/buttonStyles";
import {
  chatHeader,
  chatWindowContainer,
  mainChatWindow,
  parameterChip,
  questionButton,
  questionButtonTypography,
  selectedParameters,
} from "./chatwindowStyles";
import { useNavigate } from "react-router";
import {
  generateVerificationToken,
  getSession,
  getUserBotMemory,
} from "../../../../features/auth/authSlice";
import { set } from "lodash";
import { dialogStyles } from "../../../../Constants/dialogStyles";
import { CodeOutlined, ContentCopy } from "@mui/icons-material";
import ApiExamples from "./UserInstructions";

const ChatWindow = ({
  retrievalCombinationId,
  generationCombinationId,
  retrievalCombinationId2,
  generationCombinationId2,
  bot,
  setBot,
  setBotData,
  retrievalCombinations,
  setShowOptimizationMessage,
  showOptimizationMessage,
  handleOptimizeNow,
  handleOptimizeFurther,
  setRetrievalCombinations,
  setRetrievalCombinationId2,
  setGenerationCombinationId2,
  setTabName,
}) => {
  const { user, botMemory } = useSelector((state) => state.auth);
  const { organization } = useSelector((state) => state.organization);
  const dispatch = useDispatch();

  // Lazy‐initialize from localStorage, so we never re‐read on every render
  const [messages, setMessages] = useState([]);
  const [messagesOptimized, setMessagesOptimized] = useState([]);

  const [input, setInput] = useState(""); // Chat input
  const [inputOptimized, setInputOptimized] = useState(""); // Chat input
  const [showOptimized, setShowOptimized] = useState(true); // Optimized window visibility

  const navigate = useNavigate();

  const [loadingMessage, setLoadingMessage] = useState(false);
  const [loadingMessageOptimized, setLoadingMessageOptimized] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isCollapsedOptimized, setIsCollapsedOptimized] = useState(true);

  const chatContainerRef = useRef(null);
  const chatContainerRefOptimized = useRef(null);

  const [loading, setLoading] = useState(false);

  //  snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false); // For snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Severity of the alert
  const [snackbarDelay, setSnackbarDelay] = useState(6000);

  const tempMessageRef = useRef("");
  const tempMessageRefOptimized = useRef("");
  const windowRef = useRef(null);
  const userId = user?.user?._id?.["$oid"];

  // deploy states
  const [openCode, setOpenCode] = useState(false);
  const [key, setKeys] = useState("");
  const [verificationToken, setVerificationToken] = useState("");

  useEffect(() => {
    const apiKeys = organization?.api_keys;
    if (!key && apiKeys) {
      const keyObj = apiKeys.find((k) => k.name === bot.name);
      const key_ = keyObj ? keyObj.api_key : null;
      if (key_) {
        setKeys(key_);
      }
    }

    const verification_token = user?.user?.verification_token;
    if (verification_token && !verificationToken) {
      setVerificationToken(verification_token);
    }
  }, [organization?.api_keys]);

  // Update scroll position every time messages change
  useEffect(() => {
    scrollChatToBottomOptimized();
  }, [messagesOptimized]);

  // Update scroll position every time messages change
  useEffect(() => {
    scrollChatToBottom();
  }, [messages]);

  useEffect(() => {
    const bot_memory_id = botMemory.filter(
      (meme) => bot?._id?.["$oid"] === meme?.bot_id
    );
    const unOptimizedSessionId =
      bot_memory_id?.[0]?.memory?.sessions?.["unOptimized"]?.id?.["$oid"];
    const optimizedSessionId =
      bot_memory_id?.[0]?.memory?.sessions?.["slightlyOptimized"]?.id?.["$oid"];

    if (botMemory?.length > 0 && unOptimizedSessionId && optimizedSessionId) {
      setSnackbarMessage("Loading session memory. Please wait...");
      setSnackbarOpen(true);
      dispatch(getSession(unOptimizedSessionId))
        .unwrap()
        .then((data) => {
          const formattedMessages = data.session.messages
            .map((msg) => [
              { type: "user", text: msg.USER },
              {
                type: "system",
                text:
                  msg.AI ||
                  "Your call was interrupted. Please refresh the page after a few seconds.",
                likes: msg.likes || false, // Ensure likes are included
                dislikes: msg.dislikes || false, // Ensure dislikes are included
                feedbacks: msg.feedback || [], // Store any previous feedback
              },
            ])
            .flat(); // Flatten the array to avoid nesting

          setMessages(formattedMessages);
        })
        .catch(() => {
          setSnackbarMessage("Failed to load session.");
          setSnackbarOpen(true);
        });
      //slightlyOptimized
      dispatch(getSession(optimizedSessionId))
        .unwrap()
        .then((data) => {
          const formattedMessages = data.session.messages
            .map((msg) => [
              { type: "user", text: msg.USER },
              {
                type: "bot",
                text:
                  msg.AI ||
                  "Your call was interrupted. Please refresh the page after a few seconds.",
                likes: msg.likes || false, // Ensure likes are included
                dislikes: msg.dislikes || false, // Ensure dislikes are included
                feedbacks: msg.feedback || [], // Store any previous feedback
              },
            ])
            .flat(); // Flatten the array to avoid nesting

          setMessagesOptimized(formattedMessages);
        })
        .catch(() => {
          setSnackbarMessage("Failed to load session.");
          setSnackbarOpen(true);
        });
    }
  }, [bot, botMemory, dispatch]);

  useEffect(() => {
    const getMemory = async () => {
      const data = { user_id: userId, bot_id: bot?._id["$oid"] };
      let response = "";
      try {
        response = await dispatch(getUserBotMemory(data));
      } catch (error) {
        console.warn("No memory found, continuing without memory.");
      }
    };
    if (!loading && bot?.have_memory) {
      getMemory();
    }
  }, [bot, dispatch, loading, userId]);

  // from chatwindow
  function humanizeBotName(botName) {
    const match = botName?.match(
      /^(.+?)_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/
    );
    if (!match) return botName || "";

    const [, filename, year, month, day, hour, minute, second] = match;
    const date = new Date(+year, +month - 1, +day, +hour, +minute, +second);

    // e.g. "May 6, 2025 11:55:06"
    const formatted = date.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    return `${filename} (${formatted})`;
  }

  // Function to scroll only the chat window
  const scrollChatToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  // Function to scroll only the chat window
  const scrollChatToBottomOptimized = () => {
    if (chatContainerRefOptimized.current) {
      chatContainerRefOptimized.current.scrollTop =
        chatContainerRefOptimized.current.scrollHeight;
    }
  };

  const handleSendMessage = async (query) => {
    if (input.trim() === "" && !query) {
      alert("Please enter a message!");
      return;
    }
    if (loading || loadingMessage || loadingMessageOptimized) {
      alert("Please wait for the current process to finish.");
      return;
    }

    // Add user's message to the chat
    let userMessage;
    if (query) {
      userMessage = { type: "user", text: query };
      setInput(query);
    } else {
      if (input.trim() === "") {
        alert("Please enter a message!");
        return;
      }
      userMessage = { type: "user", text: input };
    }
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    // Add a temporary loading message for the bot's response
    const loadingSystemMessage = { type: "system", text: "gene" };
    setMessages((prevMessages) => [...prevMessages, loadingSystemMessage]);

    setInput(""); // Clear input field
    setLoadingMessage(true);

    const orgId = organization?._id?.["$oid"];
    const userId = user?.user?._id?.["$oid"];
    const botId =
      organization?.templates?.[organization?.templates?.length - 1];

    const generation_id =
      bot?.generation_combinations?.[0]?.["$oid"] || generationCombinationId;

    const token = JSON.parse(localStorage.getItem("token"));
    if (!token) {
      localStorage.setItem("user", null);
      alert("Please login again to continue");
      return;
    }
    const bot_memory_id = botMemory.filter(
      (meme) => bot._id["$oid"] === meme.bot_id
    );
    const unOptimizedSessionId =
      bot_memory_id[0]?.memory?.sessions?.["unOptimized"]?.id?.["$oid"];
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    tempMessageRef.current = ""; // Reset the temporary message
    const data = {
      query: userMessage.text,
      session_id: unOptimizedSessionId,
      flow: {
        Retrieval: bot?.combinations?.[0]?.["$oid"] || retrievalCombinationId,
        Generation: generation_id,
      },
    };

    try {
      const response = await fetch(
        `/api/eval_bot/chatend2end/retrieval/${orgId}/${userId}/${botId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          tempMessageRef.current += chunk; // Append streamed content += chunk;

          if (tempMessageRef.current === "Oops, something went wrong!") {
            const msg =
              `## Something went wrong \n` +
              `### What can you do? \n` +
              `\n We encountered an unexpected issue. \n` +
              `**Here's what you can do:** \n` +
              `- **Reload** the application. \n` +
              `- **Try again** after a few minutes.\n` +
              `- **Contact support** if the issue persists.\n`;
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              updatedMessages[updatedMessages.length - 1].text = msg;
              return updatedMessages;
            });
            done = true;
            return;
          }

          // Update the last bot message dynamically
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1].text =
              tempMessageRef.current;
            return updatedMessages;
          });
        }
      }
    } catch (error) {
      console.error("Error streaming response:", error);
      setMessages((prevMessages) => [
        ...prevMessages.slice(0, -1), // Remove the loading message
        {
          type: "system",
          text: "Error generating response. Please reload the application.",
        },
      ]);
    } finally {
      setLoadingMessage(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSendMessage(null);
    }
  };

  const handleSendMessageOptimized = async (query) => {
    if (inputOptimized.trim() === "" && !query) {
      alert("Please enter a message!");
      return;
    }

    if (loading || loadingMessage || loadingMessageOptimized) {
      alert("Please wait for the current process to finish.");
      return;
    }

    let userMessage;
    if (query) {
      userMessage = { type: "user", text: query };
      setInputOptimized(query);
    } else {
      if (inputOptimized.trim() === "") {
        alert("Please enter a message!");
        return;
      }
      userMessage = { type: "user", text: inputOptimized };
    }
    setMessagesOptimized((prevMessages) => [...prevMessages, userMessage]);

    // Add a temporary loading message for the bot's response
    const loadingSystemMessage = { type: "system", text: "gene" };
    setMessagesOptimized((prevMessages) => [
      ...prevMessages,
      loadingSystemMessage,
    ]);
    setInput("");
    setInputOptimized(""); // Clear input field
    setLoadingMessageOptimized(true);
    const orgId = organization?._id?.["$oid"];
    const userId = user?.user?._id?.["$oid"];
    const botId =
      organization?.templates?.[organization?.templates?.length - 1];
    const __GENERATION_ID_2__ =
      generationCombinationId2 || generationCombinationId;

    const token = JSON.parse(localStorage.getItem("token"));
    if (!token) {
      localStorage.setItem("user", null);
      alert("Please login again to continue");
      return;
    }
    const bot_memory_id = botMemory.filter(
      (meme) => bot._id["$oid"] === meme.bot_id
    );

    const optimizedSessionId =
      bot_memory_id[0]?.memory?.sessions?.["slightlyOptimized"]?.id?.["$oid"];
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    tempMessageRefOptimized.current = ""; // Reset the temporary message

    const data = {
      query: userMessage.text,
      session_id: optimizedSessionId,
      flow: {
        Retrieval: retrievalCombinationId2,
        Generation: __GENERATION_ID_2__,
      },
    };

    try {
      const response = await fetch(
        `/api/eval_bot/chatend2end/retrieval/${orgId}/${userId}/${botId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          tempMessageRefOptimized.current += chunk; // Append streamed content
          if (
            tempMessageRefOptimized.current === "Oops, something went wrong!"
          ) {
            const msg =
              `## Something went wrong \n` +
              `### What can you do? \n` +
              `\n We encountered an unexpected issue. \n` +
              `**Here's what you can do:** \n` +
              `- **Reload** the application. \n` +
              `- **Try again** after a few minutes.\n` +
              `- **Contact support** if the issue persists.\n`;
            setMessagesOptimized((prevMessages) => {
              const updatedMessages = [...prevMessages];
              updatedMessages[updatedMessages.length - 1].text = msg;
              return updatedMessages;
            });
            done = true;
            return;
          }
          // Update the last bot message dynamically
          setMessagesOptimized((prevMessages) => {
            const updatedMessages = [...prevMessages];
            updatedMessages[updatedMessages.length - 1].text =
              tempMessageRefOptimized.current;
            return updatedMessages;
          });
        }
      }

      // Add the response to the botData golden responses
      setBotData((prevData) => ({
        ...prevData,
        golden_responses: [
          ...prevData.golden_responses,
          { question: inputOptimized, answer: tempMessageRefOptimized.current },
        ],
      }));
    } catch (error) {
      console.error("Error streaming response:", error);
      setMessagesOptimized((prevMessages) => [
        ...prevMessages.slice(0, -1), // Remove the loading message
        {
          type: "system",
          text: "Error generating response. Please reload the application.",
        },
      ]);
    } finally {
      setLoadingMessageOptimized(false);
    }
  };

  const handleKeyDownOptimized = (e) => {
    if (e.key === "Enter") {
      handleSendMessageOptimized(null);
    }
  };

  const handleToggleCollapsed = async () => {
    if (
      bot &&
      (retrievalCombinations?.length === 0 || !retrievalCombinations)
    ) {
      const responseRetrievalCombination = await dispatch(
        getCombinations({
          id: bot?._id["$oid"],
          items: 100,
          from: 0,
          data: {},
        })
      );

      setRetrievalCombinations(
        responseRetrievalCombination?.payload?.combinations || []
      );
    }
    setIsCollapsed((prev) => !prev);
  };

  const handleToggleCollapsedOptimized = async () => {
    if (retrievalCombinations?.length === 1) {
      const responseRetrievalCombination = await dispatch(
        getCombinations({
          id: organization.optimizedCombinations[bot?._id["$oid"]],
          items: 100,
          from: 0,
          data: {},
        })
      );

      setRetrievalCombinations(
        responseRetrievalCombination?.payload?.combinations || []
      );
    }
    setIsCollapsedOptimized((prev) => !prev);
  };

  const generateKey = async () => {
    if (bot) {
      const orgId = organization?._id["$oid"];
      const data = { bot_id: bot._id["$oid"], orgId };
      const res = await dispatch(generate_APIKey(data));
      if (res.meta.requestStatus === "fulfilled") {
        setSnackbarMessage("API key successfully generated!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setKeys(res.payload.api_key);
        setOpenCode(true);
      } else {
        setSnackbarMessage("API key was not generated successfully!");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  };

  const handleGenerateToken = async () => {
    const res = await dispatch(
      generateVerificationToken({ email: user.user.email })
    );
    setVerificationToken(res.payload);
    setOpenCode(true);
  };

  const handleMakeDefault = async () => {
    // const data = { bot_id: bot_id, combination_id: combination._id["$oid"] };
    // const response = await dispatch(makeDefault(data));
    const userId = user?.user?._id?.["$oid"];

    const default_dict = {
      retrieval: retrievalCombinationId2,
      generation: generationCombinationId2 || generationCombinationId,
    };
    const data = {
      bot_id: bot?._id?.["$oid"],
      default_dict,
      user_id: userId,
    };

    try {
      const result = await dispatch(saveDefaultCombinations(data));
      if (result.meta.requestStatus === "fulfilled") {
        setSnackbarMessage("Application deployment has started, please wait."); // Show success message
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          generateKey();
          setBot({
            ...bot,
            default_flow: {
              1: {
                retrieval: default_dict.retrieval,
                generation: default_dict.generation,
              },
            },
          });
        }, 500);
        setTimeout(() => {
          handleGenerateToken();
        }, 500);
      } else {
        setSnackbarMessage("Failed to deploy application."); // Handle any non-fulfilled case
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage("An error occurred while deploying the application.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true); // Open the snackbar in both cases
    }
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCopy = async (command) => {
    try {
      await navigator.clipboard.writeText(command);
      // optionally show a “Copied!” tooltip/snackbar here
      setSnackbarMessage("Command copied to clipboard!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy!", err);
      setSnackbarMessage("Failed to copy command.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  return !loading && bot?.initialised === "unsuccessful" ? (
    <Box sx={chatWindowContainer}>
      <Typography>
        Application "<strong>{humanizeBotName(bot?.name)}"</strong> setup has
        failed. Please try again.
      </Typography>
    </Box>
  ) : !loading &&
    (bot?.initialised === "initialising" ||
      bot.initialised === "not started") ? (
    <Box sx={chatWindowContainer}>
      <Box className="loader_" sx={loaderStyles}>
        {/* CircularProgress spinner */}
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ color: "var(--buttonBackground)", marginRight: 2 }}
        />
        <Typography>
          Application "<strong>{humanizeBotName(bot?.name)}"</strong> setup is
          in progress. Reload the page in few minutes to check the status.
        </Typography>
      </Box>
    </Box>
  ) : (
    <Box sx={chatWindowContainer}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { xs: "90%", md: messages.length > 0 ? "80%" : "67%" },
          width: "100%",
        }}
      >
        {/* Open Code Dialog */}
        <Dialog
          open={openCode}
          onClose={() => setOpenCode(false)}
          maxWidth={"lg"}
          PaperProps={{ sx: dialogStyles.dialogRoot }}
        >
          <DialogTitle sx={dialogStyles.dialogTitle}>
            How to use deployed LLM application?
          </DialogTitle>
          <DialogContent sx={dialogStyles.dialogContent}>
            <DialogContentText sx={dialogStyles.dialogContentText}>
              <ApiExamples
                apiKey={key}
                verificationToken={verificationToken}
                query={`"${bot?.golden_responses?.[0]?.question}"`}
                handleCopy={(text) => {
                  navigator.clipboard.writeText(text);
                  setSnackbarMessage("Command copied to clipboard!");
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                }}
              />
            </DialogContentText>
          </DialogContent>

          <DialogActions sx={dialogStyles.dialogActions}>
            <Button sx={textButtonStyles} onClick={() => setOpenCode(false)}>
              Close
            </Button>
            <Button sx={primaryButtonStyles} onClick={() => setOpenCode(false)}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
        <div ref={windowRef} />
        <Snackbar
          open={showOptimizationMessage}
          onClose={() => setShowOptimizationMessage(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={99999}
        >
          <SnackbarContent
            message={
              "Not satisfied with the answers? Optimize your LLM application now."
            }
            action={
              !showOptimized && (
                <Button onClick={handleOptimizeNow} sx={{ color: "#fff" }}>
                  Optimize Now
                </Button>
              )
            }
          />
        </Snackbar>
        {/* Selected parameters */}
        <Box
          sx={{
            ...selectedParameters,
            display: { xs: "none", md: "flex" },
            width: isCollapsed ? "1px" : "16%",
            backgroundColor: theme.main.applicationBackground,
            border: isCollapsed ? "none" : `1px solid ${theme.border.light}`,
            paddingLeft: isCollapsed ? "0px" : "10px",
            paddingRight: isCollapsed ? "4px" : "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              marginLeft: isCollapsed ? "20px" : "0px",
            }}
          >
            {" "}
            <Tooltip title="Selected parameters">
              <IconButton
                onClick={handleToggleCollapsed}
                sx={{ ...iconButtonActive, width: "20px", height: "20px" }}
              >
                {isCollapsed ? (
                  <KeyboardArrowRightIcon />
                ) : (
                  <KeyboardArrowLeftIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          {/* Selected Parameters */}
          {!isCollapsed && (
            <Box>
              <Typography variant="h5" gutterBottom noWrap fontSize={16}>
                Selected Params
              </Typography>

              {retrievalCombinations &&
                retrievalCombinations?.length > 0 &&
                Object.keys(retrievalCombinations?.[0] || {})?.length > 0 && (
                  <Box display="flex" flexDirection="column">
                    {[
                      {
                        heading: "Chunk Size",
                        value: retrievalCombinations[0]["chunksize"],
                        tooltip:
                          "Chunking divides text into smaller segments for easier management and retrieval. The chunk size determines the length of these segments.",
                      },
                      {
                        heading: "Embedding Model",
                        value: retrievalCombinations[0]["embedding_model"],
                        tooltip:
                          "Embedding models convert text into numerical vectors to capture semantic meaning, essential for retrieval and classification tasks.",
                      },
                      {
                        heading: "Distance Metric",
                        value: retrievalCombinations[0]["metric"],
                        tooltip:
                          "Metrics determine how similarity or distance between vectors is calculated, crucial for effective retrieval.",
                      },
                      {
                        heading: "Document Parser",
                        value: retrievalCombinations[0]["parser"],
                        tooltip:
                          "Document parsers process and extract relevant data from documents, tailored to your specific data type and use case.",
                      },
                      {
                        heading: "Query Preprocessing Method",
                        value:
                          retrievalCombinations[0]["query_preprocess_method"],
                        tooltip:
                          "Query transformations refine user queries to enhance retrieval effectiveness, addressing data and query complexities.",
                      },
                      {
                        heading: "Reranker",
                        value: retrievalCombinations[0]["rerank_method"],
                        tooltip:
                          "Reranking methods reorder search results to prioritize the most relevant documents.",
                      },
                      {
                        heading: "Retrieval Method",
                        value: retrievalCombinations[0]["retrieval_method"],
                        tooltip:
                          "Retrieval methods define the approach for finding relevant documents based on user queries.",
                      },
                      {
                        heading: "Top K",
                        value: retrievalCombinations[0]["top_k"],
                        tooltip:
                          "Top K limits the number of retrieved documents to the most relevant ones, defined by the value of K.",
                      },
                    ].map((item, index) => (
                      <Box key={index} sx={parameterChip}>
                        <Tooltip title={item.tooltip} arrow placement="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            fontSize={11}
                            color="#fff"
                            noWrap
                            sx={{ marginBottom: "2px" }}
                          >
                            {item.heading}
                          </Typography>
                        </Tooltip>
                        <Typography
                          noWrap
                          variant="body2"
                          fontSize={9}
                          color="#aaa"
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
            </Box>
          )}
        </Box>

        {/* general all snackbar */}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarDelay} // Closes after 6 seconds
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Position of the snackbar
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Main Chat Window */}
        {messages.length === 0 ? (
          <Box
            id="chatwindow"
            sx={{
              ...mainChatWindow,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              ml: { xs: "0px", md: "20px" },
              pt: 0,
              borderRight: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              borderTop: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              borderLeft: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              borderBottom: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              marginRight: "10px",
              borderRadius: "20px",
            }}
          >
            {/* Named Box at the Top */}
            <Box sx={chatHeader}>
              <Typography
                variant="p"
                sx={{
                  padding: "5px",
                  borderRadius: "5px",
                  paddingRight: "20px",
                  // backgroundColor: "#bc6970",
                  fontSize: createLLMTypography.fontSizes.sectionText1,
                }}
              >
                {bot?.combinations?.length > 1 &&
                !organization?.optimizedCombinations?.[bot?._id?.["$oid"]]
                  ? "Slightly Optimized Version"
                  : "Unoptimized Version"}
              </Typography>
            </Box>

            {/* Input Box */}
          </Box>
        ) : (
          <Box
            id="chatwindow"
            sx={{
              ...mainChatWindow,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              marginLeft: { xs: 0, md: "18px" },
              pt: 0,
              borderRight: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              borderTop: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              borderLeft: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              borderBottom: {
                xs: "none",
                md: `1px solid ${theme.border.light}`,
              },
              marginRight: "10px",
              borderRadius: "20px",
            }}
          >
            {/* Chat Header */}
            <Box sx={chatHeader}>
              <Typography
                variant="p"
                sx={{
                  padding: "5px",
                  borderRadius: "5px",
                  paddingRight: "20px",
                  // backgroundColor: "#572A2E",
                  fontSize: createLLMTypography.fontSizes.sectionText1,
                }}
              >
                {bot?.combinations?.length > 1 &&
                !organization?.optimizedCombinations?.[bot?._id?.["$oid"]]
                  ? "Slightly Optimized Version"
                  : "Unoptimized Version"}
              </Typography>
            </Box>
            <Box
              ref={chatContainerRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                marginBottom: 2,
                marginTop: 7,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
                width: "99%",
                padding: "0px 10px",
                ...scrollbarStyles,
              }}
            >
              {messages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    alignSelf:
                      message.type === "user" ? "flex-end" : "flex-start",
                    backgroundColor:
                      message.type === "user" ? "#3C3F51" : "#27293D",
                    borderRadius: 2,
                    padding: message.text === "gene" ? 2 : "0px 20px",
                    maxWidth: message.type === "user" ? "60%" : "95%",
                    fontSize: { xs: 11, md: 14 },
                  }}
                >
                  {message.text === "gene" ? (
                    <Box className="bubble-animation-container">
                      <Box className="bubble-animation"></Box>
                      <Box className="bubble-animation"></Box>
                      <Box className="bubble-animation"></Box>
                    </Box>
                  ) : typeof message.text === "string" ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.text}
                    </ReactMarkdown>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: { xs: 11, md: 14 },
                      }}
                      variant="body1"
                      color="#fff"
                    >
                      {message.text}
                    </Typography>
                  )}

                  {!loadingMessage &&
                    message.type !== "user" &&
                    !showOptimized && (
                      <Typography variant="body2" color="#aaa">
                        Not happy with the response?
                        <Button
                          onClick={handleOptimizeNow}
                          sx={{ color: "#fff", textTransform: "none" }}
                        >
                          (Optimize Now)
                        </Button>
                      </Typography>
                    )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Optimized Chat Window  */}
        {(showOptimized ||
          (retrievalCombinationId2 && generationCombinationId2)) &&
          organization?.optimizedCombinations?.[bot?._id?.["$oid"]] &&
          (messages.length === 0 && messagesOptimized.length === 0 ? (
            <Box
              sx={{
                ...mainChatWindow,
                justifyContent: "center",
                alignItems: "center",
                borderLeft: {
                  xs: "none",
                  md: `1px solid ${theme.border.light}`,
                },
                borderTop: `1px solid ${theme.border.light}`,

                borderRight: {
                  xs: "none",
                  md: `1px solid ${theme.border.light}`,
                },
                borderBottom: {
                  xs: "none",
                  md: `1px solid ${theme.border.light}`,
                },
                borderRadius: "20px",
                marginRight: isCollapsedOptimized ? "0px" : "20px",
                pt: 0,
              }}
            >
              {/* Chat Header */}
              <Box
                sx={{
                  ...chatHeader,
                }}
              >
                <Typography
                  variant="p"
                  sx={{
                    padding: "5px",
                    borderRadius: "5px",
                    paddingRight: "20px",
                    marginRight: "20px",
                    ml: 1,
                    // backgroundColor: "#2F3F29",
                    fontSize: createLLMTypography.fontSizes.sectionText1,
                  }}
                >
                  Slightly Optimized Version
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                ...mainChatWindow,
                justifyContent: "center",
                alignItems: "center",
                borderLeft: {
                  xs: "none",
                  md: `1px solid ${theme.border.light}`,
                },
                borderTop: `1px solid ${theme.border.light}`,

                borderRight: {
                  xs: "none",
                  md: `1px solid ${theme.border.light}`,
                },
                borderBottom: {
                  xs: "none",
                  md: `1px solid ${theme.border.light}`,
                },
                borderRadius: "20px",
                marginRight: isCollapsedOptimized ? "0px" : "20px",
                pt: 0,
              }}
            >
              {/* Chat Header */}
              <Box
                sx={{
                  ...chatHeader,
                }}
              >
                <Typography
                  variant="p"
                  sx={{
                    padding: "5px",
                    borderRadius: "5px",
                    paddingRight: "20px",
                    marginRight: "20px",
                    ml: 1,
                    // backgroundColor: "#2F3F29",
                    fontSize: createLLMTypography.fontSizes.sectionText1,
                  }}
                >
                  Slightly Optimized Version
                </Typography>
              </Box>
              <Box
                ref={chatContainerRefOptimized}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  marginBottom: 2,
                  marginTop: 7,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  width: "99%",
                  padding: "0px 10px",
                  WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
                  ...scrollbarStyles,
                }}
              >
                {messagesOptimized.map((message, index) => (
                  <Box
                    key={index}
                    sx={{
                      alignSelf:
                        message.type === "user" ? "flex-end" : "flex-start",
                      backgroundColor:
                        message.type === "user" ? "#3C3F51" : "#27293D",
                      borderRadius: 2,
                      padding: message.text === "gene" ? 2 : "0px 20px",
                      maxWidth: message.type === "user" ? "60%" : "95%",
                      fontSize: { xs: 11, md: 14 },
                    }}
                  >
                    {message.text === "gene" ? (
                      <Box className="bubble-animation-container">
                        <Box className="bubble-animation"></Box>
                        <Box className="bubble-animation"></Box>
                        <Box className="bubble-animation"></Box>
                      </Box>
                    ) : (
                      <>
                        {typeof message.text === "string" ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.text}
                          </ReactMarkdown>
                        ) : (
                          <Typography
                            sx={{ fontSize: { xs: 11, md: 14 } }}
                            variant="body1"
                            color="#fff"
                          >
                            {message.text}
                          </Typography>
                        )}
                        {message.type !== "user" &&
                          index === messagesOptimized.length - 1 && (
                            <Box
                              sx={{
                                my: 1,
                                backgroundColor: theme.border.light,
                              }}
                            >
                              <Divider />
                            </Box>
                          )}
                        {message.type !== "user" &&
                          index === messagesOptimized.length - 1 && (
                            <Typography
                              variant="body2"
                              color="#aaa"
                              width={"100%"}
                              display={"flex"}
                              flexDirection={"row"}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                              sx={{ fontSize: { xs: 11, md: 14 } }}
                            >
                              Still not happy with the response?
                              {showOptimized && (
                                <Button
                                  onClick={handleOptimizeFurther}
                                  sx={{
                                    ...primaryButtonStyles,
                                    width: { xs: "100px", md: "140px" },
                                    fontSize: { xs: 11, md: 14 },
                                  }}
                                >
                                  Optimize Further
                                </Button>
                              )}
                            </Typography>
                          )}
                        {message.type !== "user" &&
                          index === messagesOptimized.length - 1 && (
                            <Typography
                              variant="body2"
                              color="#aaa"
                              width={"100%"}
                              display={"flex"}
                              flexDirection={"row"}
                              justifyContent={"space-between"}
                              alignItems={"center"}
                              sx={{ fontSize: { xs: 11, md: 14 } }}
                            >
                              {bot?.default_flow?.default_dict?.retrieval
                                ? "Application already deployed."
                                : "Like these responses?"}
                              {showOptimized &&
                                (bot?.default_flow?.[1]?.retrieval ? (
                                  <Button
                                    sx={{
                                      ...primaryButtonStyles,
                                      my: 1,
                                      width: { xs: "100px", md: "140px" },
                                      fontSize: { xs: 11, md: 14 },
                                    }}
                                    onClick={() => {
                                      setOpenCode(true);
                                    }}
                                  >
                                    View user guide
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={handleMakeDefault}
                                    sx={{
                                      ...primaryButtonStyles,
                                      my: 1,
                                      width: { xs: "100px", md: "140px" },
                                      fontSize: { xs: 11, md: 14 },
                                    }}
                                  >
                                    Deploy
                                  </Button>
                                ))}
                            </Typography>
                          )}
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

        {/* Selected parameters */}
        {(showOptimized ||
          (retrievalCombinationId2 && generationCombinationId2)) &&
          organization?.optimizedCombinations?.[bot?._id?.["$oid"]] && (
            <Box
              sx={{
                ...selectedParameters,
                display: { xs: "none", md: "flex" },
                width: isCollapsedOptimized ? "10px" : "16%",
                backgroundColor: theme.main.applicationBackground,
                border: isCollapsedOptimized
                  ? "none"
                  : `1px solid ${theme.border.light}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  marginRight: isCollapsedOptimized ? "25px" : "0px",
                }}
              >
                {" "}
                <Tooltip title="Selected parameters for slightly optimized version">
                  <IconButton
                    onClick={handleToggleCollapsedOptimized}
                    sx={{
                      ...iconButtonActive,
                      marginLeft: isCollapsedOptimized ? "-20px" : "0px",
                      width: "20px",
                      height: "20px",
                    }}
                  >
                    {isCollapsedOptimized ? (
                      <KeyboardArrowLeftIcon />
                    ) : (
                      <KeyboardArrowRightIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Selected Parameters Optimized bot*/}
              {!isCollapsedOptimized && (
                <Box>
                  <Typography noWrap variant="h5" fontSize={16} gutterBottom>
                    Selected Params
                  </Typography>

                  {retrievalCombinations &&
                    retrievalCombinations.length > 0 &&
                    Object.keys(
                      retrievalCombinations?.[
                        (retrievalCombinations || [])?.findIndex(
                          (comb) =>
                            comb?._id["$oid"] ===
                            organization?.optimizedCombinations[
                              comb?.combination_set["$oid"]
                            ]
                        )
                      ] || {}
                    ).length > 0 && (
                      <Box display="flex" flexDirection="column">
                        {[
                          {
                            heading: "Chunk Size",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["chunksize"],
                            tooltip:
                              "Chunking divides text into smaller segments for easier management and retrieval. The chunk size determines the length of these segments.",
                          },
                          {
                            heading: "Embedding Model",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["embedding_model"],
                            tooltip:
                              "Embedding models convert text into numerical vectors to capture semantic meaning, essential for retrieval and classification tasks.",
                          },
                          {
                            heading: "Distance Metric",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["metric"],
                            tooltip:
                              "Metrics determine how similarity or distance between vectors is calculated, crucial for effective retrieval.",
                          },
                          {
                            heading: "Document Parser",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["parser"],
                            tooltip:
                              "Document parsers process and extract relevant data from documents, tailored to your specific data type and use case.",
                          },
                          {
                            heading: "Query Preprocessing Method",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["query_preprocess_method"],
                            tooltip:
                              "Query transformations refine user queries to enhance retrieval effectiveness, addressing data and query complexities.",
                          },
                          {
                            heading: "Reranker",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["rerank_method"],
                            tooltip:
                              "Reranking methods reorder search results to prioritize the most relevant documents.",
                          },
                          {
                            heading: "Retrieval Method",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["retrieval_method"],
                            tooltip:
                              "Retrieval methods define the approach for finding relevant documents based on user queries.",
                          },
                          {
                            heading: "Top K",
                            value:
                              retrievalCombinations[
                                (retrievalCombinations || [])?.findIndex(
                                  (comb) =>
                                    comb._id["$oid"] ===
                                    organization?.optimizedCombinations[
                                      comb?.combination_set["$oid"]
                                    ]
                                )
                              ]["top_k"],
                            tooltip:
                              "Top K limits the number of retrieved documents to the most relevant ones, defined by the value of K.",
                          },
                        ].map((item, index) => (
                          <Box key={index} sx={parameterChip}>
                            <Tooltip
                              title={item.tooltip}
                              arrow
                              placement="right"
                            >
                              <Typography
                                noWrap
                                variant="body2"
                                fontWeight="bold"
                                fontSize={11}
                                color="#fff"
                                sx={{ marginBottom: "2px" }}
                              >
                                {item.heading}
                              </Typography>
                            </Tooltip>
                            <Typography
                              noWrap
                              variant="body2"
                              fontSize={9}
                              color="#aaa"
                            >
                              {item.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          )}
      </Box>

      {/* Centered Typography */}

      {bot &&
        messages.length === 0 &&
        messagesOptimized.length === 0 &&
        bot?.golden_responses &&
        bot?.golden_responses?.length > 0 && (
          <Box sx={questionButton}>
            {bot?.golden_responses?.map((qna, index) => {
              return (
                index < 3 && (
                  <Typography
                    fontSize={14}
                    sx={questionButtonTypography}
                    onClick={() => {
                      handleSendMessage(qna.question);
                      handleSendMessageOptimized(qna.question);
                    }}
                  >
                    {qna.question}
                  </Typography>
                )
              );
            })}
          </Box>
        )}
      {/* Text input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "center",
          padding: "2px",
          paddingRight: 1,
          borderRadius: 2,
          mt: 1,
          width: "97%",
          backgroundColor: "#121212",
          border: `1px solid ${theme.border.light}`,
          "&:hover": { borderColor: "#777" },
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Ask a question over your data"
          value={input}
          fullWidth
          onKeyDown={(e) => {
            handleKeyDown(e);
            handleKeyDownOptimized(e);
          }}
          onChange={(e) => {
            setInput(e.target.value);
            setInputOptimized(e.target.value);
          }}
          sx={{
            backgroundColor: "#121212",
            input: { color: "#fff" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none", // Remove the border
              },
              "&:hover fieldset": {
                border: "none", // Remove hover border
              },
              "&.Mui-focused fieldset": {
                border: "none", // Remove focused border
              },
            },
            "& .MuiFormHelperText-root": {
              color: "var(--bgLight)", // Helper text color
            },
          }}
        />
        <IconButton
          variant="contained"
          onClick={() => {
            handleSendMessage(null);
            handleSendMessageOptimized(null);
          }}
          disabled={
            loading || input.trim() === "" || inputOptimized.trim() === ""
          }
          sx={{
            ...iconButtonActive,
            marginLeft: 1,
            backgroundColor: "none",
            fontSize: "13px",
            "&:hover": { backgroundColor: "#2980b9" },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatWindow;
